import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, CircularProgress,
  Divider, Alert, Button, LinearProgress, Tooltip, Table, TableHead,
  TableRow, TableCell, TableBody, Paper, FormControl, InputLabel, Select, MenuItem,
  ToggleButtonGroup, ToggleButton, TextField
} from '@mui/material';
import {
  Phone, AttachMoney, TrendingUp, TrendingDown, People, Speed,
  Refresh, BarChart, Calculate, CalendarMonth, AccountBalance
} from '@mui/icons-material';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  Legend, ResponsiveContainer, BarChart as ReBarChart, Bar
} from 'recharts';
import api from '../../services/api';
import { useAuthStore, financialHidden } from '../../store/auth';
import { DashboardProjection } from './DashboardProjection';
import { COUNTRY_LIST } from '../../utils/dialCountry';

const fmt  = (n: number) => n?.toLocaleString() ?? '0';
// null/undefined => '—' : money is masked server-side for the sub_super_admin role
const fmtM = (n: number) => (n === null || n === undefined) ? '—' : '$' + Number(n).toFixed(2);
const fmtPct = (n: number) => (n === null || n === undefined) ? '—' : Number(n).toFixed(1) + '%';

// ── Period switch helpers (UTC months; data begins July 2026) ───────────────
const FIRST_MONTH = '2026-07';
const currentMonth = () => { const d = new Date(); return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`; };
const monthOptions = (): string[] => {
  const out: string[] = []; const [fy, fm] = FIRST_MONTH.split('-').map(Number);
  const cur = currentMonth(); let y = fy, m = fm;
  for (let i = 0; i < 60; i++) {
    const key = `${y}-${String(m).padStart(2, '0')}`; out.push(key);
    if (key === cur) break;
    m += 1; if (m > 12) { m = 1; y += 1; }
  }
  return out.reverse();
};
const monthLabel = (key: string) => { const [y, m] = key.split('-').map(Number); return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }); };
const FIRST_DAY = '2026-07-01';
const today = () => new Date().toISOString().slice(0, 10);
const dayLabel = (key: string) => new Date(key + 'T00:00:00Z').toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

// ── Stat card ───────────────────────────────────────────────────────────────
const StatCard: React.FC<{ title: string; value: string; sub?: string; icon: React.ReactNode; color: string; trend?: string }> = ({ title, value, sub, icon, color, trend }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>{title}</Typography>
          <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, color }}>{value}</Typography>
          {sub && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{sub}</Typography>}
        </Box>
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: color + '18', color }}>{icon}</Box>
      </Box>
      {trend && <Chip label={trend} size="small" sx={{ mt: 1.5 }} color={trend.startsWith('+') ? 'success' : 'default'} variant="outlined" />}
    </CardContent>
  </Card>
);

export const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [trend, setTrend] = useState<any[]>([]);
  const [topCustomers, setTopCustomers] = useState<any[]>([]);
  const [topDest, setTopDest] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuthStore();
  const canWrite = user?.role !== 'executive';
  const hideMoney = financialHidden(user?.role); // ops_admin / sub_super_admin: technical-only view
  const [rerateLoading, setRerateLoading] = useState(false);
  const [rerateStats, setRerateStats] = useState<any>(null);
  const [rerateResult, setRerateResult] = useState<any>(null);

  const [filterTenant, setFilterTenant] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [tenants, setTenants] = useState<any[]>([]);
  // Period switch: 'all' (all-time, as before) or 'month' (selected calendar month; resets on the 1st)
  // Always open on today's figures (Day view); the previous selection is not restored.
  const [view, setView] = useState<'all' | 'month' | 'day'>('day');
  const [day, setDay] = useState<string>(() => today());
  const isToday = day === today();
  const [month, setMonth] = useState<string>(() => currentMonth());
  const months = monthOptions();
  const isCurrentMonth = month === currentMonth();
  const periodLabel = view === 'month' ? monthLabel(month) : view === 'day' ? dayLabel(day) : '';

  const load = useCallback(async () => {
    setLoading(true);
    const flt: Record<string, string> = {};
    if (filterTenant) flt.tenant_id = filterTenant;
    if (filterCountry) flt.country_code = filterCountry;
    if (view === 'month') { flt.view = 'month'; flt.month = month; }
    if (view === 'day') { flt.view = 'day'; flt.date = day; }
    const q = (extra: Record<string, string> = {}) => '?' + new URLSearchParams({ ...extra, ...flt }).toString();
    try {
      const [s, t, tc, td] = await Promise.all([
        api.get('/dashboard/stats' + q()),
        api.get('/dashboard/trend' + q({ days: '30' })),
        api.get('/dashboard/top-customers' + q({ limit: '8' })),
        api.get('/dashboard/top-destinations' + q({ limit: '8' })),
      ]);
      setStats(s.data);
      setTrend(t.data);
      setTopCustomers(tc.data);
      setTopDest(td.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterTenant, filterCountry, view, month, day]);

  const loadRerateStats = useCallback(async () => {
    try { const { data } = await api.get('/import/rerate/stats'); setRerateStats(data); }
    catch { }
  }, []);

  useEffect(() => { load(); if (!hideMoney) loadRerateStats(); }, [load, loadRerateStats, hideMoney]);
  useEffect(() => { api.get('/tenants?limit=500&tier=customer').then(({ data: r }) => setTenants(r.data || [])).catch(() => {}); }, []);

  const runRerate = async () => {
    setRerateLoading(true); setRerateResult(null);
    try {
      const { data } = await api.post('/import/rerate', {});
      setRerateResult(data);
      await load(); await loadRerateStats();
    } catch (e: any) { setRerateResult({ error: e.response?.data?.message || 'Re-rate failed' }); }
    finally { setRerateLoading(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  const s = stats || {};
  const unrated = parseInt(rerateStats?.unrated_answered) || 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h4" fontWeight={700}>Dashboard</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => { if (v) setView(v); }}>
            <ToggleButton value="all">All time</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="day">Day</ToggleButton>
          </ToggleButtonGroup>
          {view === 'day' && (
            <TextField size="small" type="date" label="Day" value={day} sx={{ minWidth: 160 }}
              InputLabelProps={{ shrink: true }} inputProps={{ min: FIRST_DAY, max: today() }}
              onChange={e => { const v = e.target.value; if (v && v >= FIRST_DAY && v <= today()) setDay(v); }} />
          )}
          {view === 'month' && (
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={e => setMonth(String(e.target.value))}>
                {months.map(m => <MenuItem key={m} value={m}>{monthLabel(m)}{m === currentMonth() ? ' (to date)' : ''}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Customer</InputLabel>
            <Select value={filterTenant} label="Customer" onChange={e => setFilterTenant(e.target.value)}>
              <MenuItem value="">All customers</MenuItem>
              {tenants.map((t: any) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Country</InputLabel>
            <Select value={filterCountry} label="Country" onChange={e => setFilterCountry(e.target.value)}>
              <MenuItem value="">All countries</MenuItem>
              {COUNTRY_LIST.map((c) => <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          {(filterTenant || filterCountry) && <Button size="small" color="inherit" onClick={() => { setFilterTenant(''); setFilterCountry(''); }}>Clear</Button>}
          <Button startIcon={<Refresh />} onClick={load} variant="outlined" size="small">Refresh</Button>
        </Box>
      </Box>

      {view === 'day' && (
        <Alert icon={<CalendarMonth fontSize="inherit" />} severity="info" sx={{ mb: 2 }}>
          Showing <strong>{dayLabel(day)}</strong>{isToday ? ` — today so far (${s.period?.elapsed_pct ?? ''}% of the day)` : ' — full day'}.
          {!hideMoney && s.period?.channel_rental && (
            <> Channel rental for the day <strong>{fmtM(s.period.channel_rental.accrued)}</strong> (1/{s.period.days_in_month} of {fmtM(s.period.channel_rental.applicable_monthly)} / month); profit below is shown gross and net of it.</>
          )}
        </Alert>
      )}

      {view === 'month' && (
        <Alert icon={<CalendarMonth fontSize="inherit" />} severity="info" sx={{ mb: 2 }}>
          Showing <strong>{monthLabel(month)}</strong>{isCurrentMonth ? ` — month to date (day ${s.period?.day_of_month ?? ''} of ${s.period?.days_in_month ?? ''})` : ' — closed month'}.
          {!hideMoney && s.period?.channel_rental && (
            <> Channel rental accrued <strong>{fmtM(s.period.channel_rental.accrued)}</strong> of {fmtM(s.period.channel_rental.applicable_monthly)} for the month; profit below is shown gross and net of it.</>
          )}
        </Alert>
      )}

      {/* Re-rate banner */}
      {unrated > 0 && canWrite && !hideMoney && (
        <Alert severity="warning" sx={{ mb: 3 }}
          action={
            <Button size="small" variant="contained" color="warning" startIcon={rerateLoading ? <CircularProgress size={14}/> : <Calculate />}
              onClick={runRerate} disabled={rerateLoading}>
              {rerateLoading ? 'Rating...' : `Re-rate ${unrated.toLocaleString()} CDRs`}
            </Button>
          }>
          <strong>{unrated.toLocaleString()} answered CDRs</strong> have no revenue calculated.
          Current revenue: <strong>{fmtM(parseFloat(rerateStats?.current_revenue))}</strong>.
          Re-rate to calculate sell/buy costs from your rate plans.
        </Alert>
      )}
      {rerateResult && !rerateResult.error && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setRerateResult(null)}>
          Re-rated <strong>{rerateResult.updated?.toLocaleString()}</strong> CDRs.
          Revenue: <strong>{fmtM(rerateResult.total_revenue)}</strong> |
          Cost: <strong>{fmtM(rerateResult.total_cost)}</strong> |
          Profit: <strong>{fmtM(rerateResult.total_profit)}</strong>
        </Alert>
      )}
      {rerateResult?.error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setRerateResult(null)}>{rerateResult.error}</Alert>}

      {/* ── Stat cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {hideMoney && <>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Total Calls" value={fmt(s.total_calls)} sub={`${fmt(s.answered_calls)} answered`} icon={<Phone />} color="#2e7d32" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="ASR" value={s.total_calls > 0 ? fmtPct((s.answered_calls / s.total_calls) * 100) : '—'} sub="Answered / attempted" icon={<TrendingUp />} color={s.total_calls > 0 && s.answered_calls / s.total_calls < 0.3 ? '#c62828' : '#1565c0'} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="ACD" value={s.answered_calls > 0 ? `${((s.total_minutes * 60) / s.answered_calls).toFixed(1)} s` : '—'} sub="Average answered call duration" icon={<Speed />} color="#00838f" />
          </Grid>
        </>}
        {!hideMoney && <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Revenue" value={fmtM(s.total_revenue)} sub={`${fmt(s.total_calls)} total calls`} icon={<AttachMoney />} color="#2e7d32" />
        </Grid>}
        {!hideMoney && <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Cost" value={fmtM(s.total_cost)} sub="Carrier termination costs" icon={<TrendingDown />} color="#c62828" />
        </Grid>}
        {!hideMoney && <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Gross Profit" value={fmtM(s.total_profit)} sub={`Margin: ${fmtPct(s.margin_pct)}`} icon={<TrendingUp />} color="#1565c0" />
        </Grid>}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Minutes" value={fmt(s.total_minutes)} sub={`${fmt(s.answered_calls)} answered calls`} icon={<Phone />} color="#6a1b9a" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Active Carriers" value={fmt(s.active_carriers)} sub="SIP trunks configured" icon={<Speed />} color="#e65100" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Customers" value={fmt(s.total_tenants)} sub="Active tenants" icon={<People />} color="#00695c" />
        </Grid>
        {!hideMoney && <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Margin %" value={fmtPct(s.margin_pct)} sub="Revenue vs Cost" icon={<BarChart />} color={s.margin_pct > 20 ? '#2e7d32' : s.margin_pct > 0 ? '#ed6c02' : '#c62828'} />
        </Grid>}
        {!hideMoney && view === 'all' && <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Avg Rate / Min" value={s.total_minutes > 0 ? '$' + (s.total_revenue / s.total_minutes).toFixed(4) : '$0.0000'} sub="Revenue per minute" icon={<Calculate />} color="#37474f" />
        </Grid>}
        {!hideMoney && view !== 'all' && s.period?.channel_rental && <>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Channel Rental (accrued)" value={fmtM(s.period.channel_rental.accrued)}
              sub={view === 'day' ? `${fmtM(s.period.channel_rental.applicable_daily)} / day · ${s.period.elapsed_pct}% of day` : `of ${fmtM(s.period.channel_rental.applicable_monthly)} / month · ${s.period.elapsed_pct}% of month`} icon={<AccountBalance />} color="#8e24aa" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Net Profit" value={fmtM(s.period.net_profit)} sub={`After channel rental · net margin ${fmtPct(s.period.net_margin_pct)}`}
              icon={<TrendingUp />} color={s.period.net_profit >= 0 ? '#2e7d32' : '#c62828'} />
          </Grid>
        </>}
      </Grid>

      {/* ── Projection (financial — hidden for technical-only roles) ── */}
      {!hideMoney && (view === 'all' || (view === 'month' && isCurrentMonth) || (view === 'day' && isToday)) && <DashboardProjection tenantId={filterTenant} countryCode={filterCountry} />}

      {/* ── Technical chart for finance-blind roles: attempts, answered, ASR ── */}
      {hideMoney && <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={2}>Calls & ASR — {view === 'month' ? monthLabel(month) : view === 'day' ? `30 days to ${dayLabel(day)}` : 'Last 30 Days'}</Typography>
          <ResponsiveContainer width="100%" height={260}>
            <ReBarChart data={trend.map((t: any) => ({ ...t, asr: t.calls > 0 ? +((t.answered / t.calls) * 100).toFixed(1) : 0 }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tickFormatter={d => d?.slice(5)} tick={{ fontSize: 11 }} />
              <YAxis yAxisId="c" tick={{ fontSize: 11 }} tickFormatter={v => Number(v).toLocaleString()} />
              <YAxis yAxisId="p" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => v + '%'} />
              <ReTooltip formatter={(v: any, n: any) => n === 'ASR' ? v + '%' : Number(v).toLocaleString()} />
              <Legend />
              <Bar yAxisId="c" dataKey="calls" fill="#90a4ae" name="Attempts" radius={[3,3,0,0]} />
              <Bar yAxisId="c" dataKey="answered" fill="#2e7d32" name="Answered" radius={[3,3,0,0]} />
              <Bar yAxisId="p" dataKey="asr" fill="#1565c0" name="ASR" radius={[3,3,0,0]} />
            </ReBarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>}

      {/* ── Charts (financial — hidden for technical-only roles) ── */}
      {!hideMoney && <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Revenue vs Cost — {view === 'month' ? monthLabel(month) : view === 'day' ? `30 days to ${dayLabel(day)}` : 'Last 30 Days'}</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" tickFormatter={d => d?.slice(5)} tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={v => '$' + Number(v).toFixed(2)} tick={{ fontSize: 11 }} />
                  <ReTooltip formatter={(v: any) => '$' + Number(v).toFixed(4)} />
                  <Legend />
                  <Area type="monotone" dataKey="revenue" stroke="#2e7d32" fill="#2e7d3218" strokeWidth={2} name="Revenue (Sell)" />
                  <Area type="monotone" dataKey="cost"    stroke="#c62828" fill="#c6282818" strokeWidth={2} name="Cost (Buy)" />
                  <Area type="monotone" dataKey="profit"  stroke="#1565c0" fill="#1565c018" strokeWidth={2} name="Gross Profit" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Monthly Summary</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <ReBarChart data={s.monthly || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={v => '$' + v} tick={{ fontSize: 10 }} />
                  <ReTooltip formatter={(v: any) => '$' + Number(v).toFixed(2)} />
                  <Legend />
                  <Bar dataKey="revenue" fill="#2e7d32" name="Revenue" radius={[3,3,0,0]} />
                  <Bar dataKey="cost"    fill="#c62828" name="Cost"    radius={[3,3,0,0]} />
                  <Bar dataKey="profit"  fill="#1565c0" name="Profit"  radius={[3,3,0,0]} />
                </ReBarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>}

      {/* ── Top Tables ── */}
      <Grid container spacing={3}>
        {!hideMoney && <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Top Customers by Revenue{periodLabel ? ` — ${periodLabel}` : ''}</Typography>
              <Table size="small">
                <TableHead><TableRow>
                  {['Customer','Calls','Revenue','Cost','Profit','Margin'].map(h =>
                    <TableCell key={h} sx={{ fontWeight:700, fontSize:12 }}>{h}</TableCell>)}
                </TableRow></TableHead>
                <TableBody>
                  {topCustomers.map((r, i) => {
                    const margin = r.revenue > 0 ? ((r.profit / r.revenue) * 100).toFixed(1) : '0.0';
                    return (
                      <TableRow key={i} hover>
                        <TableCell sx={{ fontWeight:600, maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.name}</TableCell>
                        <TableCell>{fmt(r.calls)}</TableCell>
                        <TableCell sx={{ color:'#2e7d32', fontWeight:600 }}>{fmtM(parseFloat(r.revenue))}</TableCell>
                        <TableCell sx={{ color:'#c62828' }}>{fmtM(parseFloat(r.cost))}</TableCell>
                        <TableCell sx={{ color:'#1565c0' }}>{fmtM(parseFloat(r.profit))}</TableCell>
                        <TableCell><Chip label={margin+'%'} size="small" color={parseFloat(margin)>20?'success':parseFloat(margin)>0?'warning':'error'} /></TableCell>
                      </TableRow>
                    );
                  })}
                  {topCustomers.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py:3, color:'text.secondary' }}>No CDR data yet</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>}
        <Grid item xs={12} md={hideMoney ? 12 : 6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Top Destinations by Calls{periodLabel ? ` — ${periodLabel}` : ''}</Typography>
              <Table size="small">
                <TableHead><TableRow>
                  {['Destination','Calls',...(hideMoney ? [] : ['Revenue']),'Minutes'].map(h =>
                    <TableCell key={h} sx={{ fontWeight:700, fontSize:12 }}>{h}</TableCell>)}
                </TableRow></TableHead>
                <TableBody>
                  {topDest.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontFamily:'monospace', fontWeight:600 }}>{r.destination}</TableCell>
                      <TableCell>{fmt(r.calls)}</TableCell>
                      {!hideMoney && <TableCell sx={{ color:'#2e7d32' }}>{fmtM(parseFloat(r.revenue))}</TableCell>}
                      <TableCell>{fmt(r.minutes)} min</TableCell>
                    </TableRow>
                  ))}
                  {topDest.length === 0 && <TableRow><TableCell colSpan={hideMoney ? 3 : 4} align="center" sx={{ py:3, color:'text.secondary' }}>No CDR data yet</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
